import React, { useMemo } from 'react';
import { extent as d3Extent, max as d3Max } from 'd3-array';
import { scaleBand, scaleLinear, scaleTime } from 'd3-scale';
import { area as d3Area, curveLinear, curveMonotoneX, line as d3Line } from 'd3-shape';
import { ChartMargin, NumericAxisConfig } from '../charts/chart-types';
import { chartTokens } from '../charts/chart-tokens';
import { formatCompact } from '../charts/chart-utils';
import './trend-line.scss';

export interface TrendPoint {
  /** Category key for `band` scales or date for `time` scales. */
  date: string | Date;
  /** Primary (left-axis) value. */
  value: number;
  /** Optional secondary (right-axis) value, e.g. a rate percentage. */
  rate?: number;
}

export interface TrendLineProps {
  data: TrendPoint[];
  width: number;
  height: number;
  margin?: ChartMargin;
  /**
   * `band` aligns x-positions with sibling categorical charts (e.g. BarChart);
   * `time` uses a continuous time scale.
   */
  xScaleType?: 'band' | 'time';
  primaryAxis?: NumericAxisConfig;
  /** When provided, enables the right-side axis and renders the rate line. */
  secondaryAxis?: NumericAxisConfig;
  /** Render an area fill underneath the primary line. Default `false`. */
  area?: boolean;
  curve?: 'linear' | 'monotone';
  /** Stroke dash pattern for the secondary (rate) line. Default `4 2`. */
  strokeDasharray?: string;
  primaryColor?: string;
  secondaryColor?: string;
  /** Hide the primary line; useful when overlaying a rate line on top of a BarChart. */
  showPrimary?: boolean;
  /** Render small dots on the rate line at evenly-spaced intervals. Default `false`. */
  rateDots?: boolean;
  ariaLabel: string;
  ariaDescription?: string;
}

const DEFAULT_MARGIN: ChartMargin = { top: 16, right: 48, bottom: 32, left: 48 };

const toDate = (v: string | Date): Date => (v instanceof Date ? v : new Date(v));

/**
 * Line / area chart with optional dual-axis support. Renders a primary line
 * for `value` against the left axis and, when `secondaryAxis` is supplied,
 * a dashed line for `rate` against the right axis.
 *
 * Pure rendering only — no tooltip handling or data transformation.
 */
export const TrendLine: React.FC<TrendLineProps> = ({
  data,
  width,
  height,
  margin = DEFAULT_MARGIN,
  xScaleType = 'time',
  primaryAxis,
  secondaryAxis,
  area = false,
  curve = 'monotone',
  strokeDasharray = '4 2',
  primaryColor = chartTokens.series.automated,
  secondaryColor = chartTokens.rateLine,
  showPrimary = true,
  rateDots = false,
  ariaLabel,
  ariaDescription,
}) => {
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const useBand = xScaleType === 'band';

  const bandScale = useMemo(() => {
    if (!useBand) return null;
    return scaleBand<string>()
      .domain(data.map((d) => String(d.date)))
      .range([0, innerWidth])
      .padding(0.15);
  }, [useBand, data, innerWidth]);

  const timeScale = useMemo(() => {
    if (useBand) return null;
    const dates = data.map((d) => toDate(d.date));
    const [d0, d1] = d3Extent(dates) as [Date | undefined, Date | undefined];
    return scaleTime()
      .domain([d0 ?? new Date(0), d1 ?? new Date(1)])
      .range([0, innerWidth]);
  }, [useBand, data, innerWidth]);

  const xPos = (d: TrendPoint): number => {
    if (bandScale) {
      const c = bandScale(String(d.date));
      return (c ?? 0) + bandScale.bandwidth() / 2;
    }
    return timeScale ? timeScale(toDate(d.date)) : 0;
  };

  const primaryDomain = useMemo<[number, number]>(() => {
    if (primaryAxis?.domain) return primaryAxis.domain;
    return [0, d3Max(data, (d) => d.value) ?? 0];
  }, [primaryAxis, data]);

  const primaryScale = useMemo(
    () =>
      scaleLinear()
        .domain([primaryDomain[0], primaryDomain[1] || 1])
        .nice()
        .range([innerHeight, 0]),
    [primaryDomain, innerHeight]
  );

  const secondaryDomain = useMemo<[number, number]>(() => {
    if (secondaryAxis?.domain) return secondaryAxis.domain;
    return [0, d3Max(data, (d) => d.rate ?? 0) ?? 0];
  }, [secondaryAxis, data]);

  const secondaryScale = useMemo(
    () =>
      scaleLinear()
        .domain([secondaryDomain[0], secondaryDomain[1] || 1])
        .range([innerHeight, 0]),
    [secondaryDomain, innerHeight]
  );

  const curveFn = curve === 'monotone' ? curveMonotoneX : curveLinear;

  const linePath = d3Line<TrendPoint>()
    .x(xPos)
    .y((d) => primaryScale(d.value))
    .curve(curveFn);

  const areaPath = d3Area<TrendPoint>()
    .x(xPos)
    .y0(innerHeight)
    .y1((d) => primaryScale(d.value))
    .curve(curveFn);

  const ratePath =
    secondaryAxis && data.some((d) => d.rate != null)
      ? d3Line<TrendPoint>()
          .defined((d) => d.rate != null)
          .x(xPos)
          .y((d) => secondaryScale(d.rate ?? 0))
          .curve(curveFn)(data)
      : null;

  const valueTickFormat = primaryAxis?.tickFormat ?? formatCompact;
  const rateTickFormat = secondaryAxis?.tickFormat ?? ((n: number) => `${n}%`);

  const renderPrimaryAxis = () => {
    if (primaryAxis?.show === false) return null;
    return (
      <g
        className="trend-line__axis trend-line__axis--left"
        aria-hidden="true"
      >
        {primaryScale.ticks(5).map((t) => {
          const y = primaryScale(t);
          return (
            <g key={t} transform={`translate(0,${y})`}>
              <line className="trend-line__gridline" x1={0} x2={innerWidth} />
              <text
                className="trend-line__axis-text"
                x={-8}
                dy="0.32em"
                textAnchor="end"
              >
                {valueTickFormat(t)}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  const renderSecondaryAxis = () => {
    if (!secondaryAxis || secondaryAxis.show === false) return null;
    return (
      <g
        className="trend-line__axis trend-line__axis--right"
        aria-hidden="true"
        transform={`translate(${innerWidth},0)`}
      >
        {secondaryScale.ticks(5).map((t) => {
          const y = secondaryScale(t);
          return (
            <text
              key={t}
              className="trend-line__axis-text"
              x={8}
              y={y}
              dy="0.32em"
              textAnchor="start"
            >
              {rateTickFormat(t)}
            </text>
          );
        })}
      </g>
    );
  };

  return (
    <svg
      className="trend-line"
      role="img"
      width={width}
      height={height}
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
    >
      <title>{ariaLabel}</title>
      {ariaDescription ? <desc>{ariaDescription}</desc> : null}
      <g transform={`translate(${margin.left},${margin.top})`}>
        {renderPrimaryAxis()}
        {renderSecondaryAxis()}
        {showPrimary && data.length > 0 ? (
          <>
            {area ? (
              <path
                className="trend-line__area"
                d={areaPath(data) ?? undefined}
                fill={primaryColor}
                fillOpacity={0.15}
                stroke="none"
                role="graphics-symbol"
                aria-label={`${ariaLabel} primary area`}
              />
            ) : null}
            <path
              className="trend-line__line trend-line__line--primary"
              d={linePath(data) ?? undefined}
              fill="none"
              stroke={primaryColor}
              strokeWidth={2}
              role="graphics-symbol"
              aria-label={`${ariaLabel} primary series`}
            />
          </>
        ) : null}
        {ratePath ? (
          <>
            <path
              className="trend-line__line trend-line__line--secondary"
              d={ratePath ?? undefined}
              fill="none"
              stroke={secondaryColor}
              strokeWidth={2}
              strokeDasharray={strokeDasharray}
              role="graphics-symbol"
              aria-label={`${ariaLabel} rate series`}
            />
            {rateDots
              ? (() => {
                  const interval = Math.max(1, Math.ceil(data.length / 8));
                  return data
                    .filter((_, i) => i % interval === 0)
                    .map((d, i) => (
                      <circle
                        key={i}
                        cx={xPos(d)}
                        cy={secondaryScale(d.rate ?? 0)}
                        r={3}
                        fill={secondaryColor}
                        aria-hidden="true"
                      />
                    ));
                })()
              : null}
          </>
        ) : null}
      </g>
    </svg>
  );
};

TrendLine.displayName = 'TrendLine';
